import type {
  AssortmentItem,
  AssortmentStatus,
  ChortkeFilter,
  ChortkeShowLocationState,
  ChortkeStoreState,
  StatusHelpContent
} from './types';
import type {HeaderDataPayload} from '@ds/types';

import {InfoCircleOutlined} from '@ant-design/icons';
import {BottomSheet, Modal} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {resetGetAssortmentItems} from 'store/slices/chortke/slicer';
import {getAssortmentRegion, getAssortmentVendor} from 'store/slices/chortke/thunk';

import AssortmentFilters from './components/AssortmentFilters/AssortmentFilters';
import AssortmentGuide from './components/AssortmentGuide/AssortmentGuide';
import AssortmentList from './components/AssortmentList/AssortmentList';
import StatusHelp from './components/StatusHelp/StatusHelp';
import {
  ASSORTMENT_FILTERS,
  ASSORTMENT_GUIDES,
  ASSORTMENT_STATUS_HELP,
  ASSORTMENT_TITLES
} from './constants';
import {resolveAssortmentType} from './utils';

type ChortkeShowProps = {
  headerData?: (data: HeaderDataPayload) => void;
};

const ChortkeShow: React.FC<ChortkeShowProps> = ({headerData}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {nameSpace} = useParams();
  const locationState = (location.state ?? {}) as ChortkeShowLocationState;
  const assortmentType = resolveAssortmentType(locationState.type, nameSpace);
  const vendorId = locationState.vendorId;
  const chortkeState = useAppSelector((state) => state.chortke) as ChortkeStoreState;
  const gettingAssortmentRegion = chortkeState.gettingAssortmentRegion;
  const assortmentRegionSuccess = chortkeState.assortmentRegionSuccess;
  const assortmentRegionItems = chortkeState.assortmentRegionItems ?? [];
  const gettingAssortmentVendor = chortkeState.gettingAssortmentVendor;
  const assortmentVendorSuccess = chortkeState.assortmentVendorSuccess;
  const assortmentVendorItems = chortkeState.assortmentVendorItems ?? [];
  const [filters, setFilters] = useState<ChortkeFilter[]>([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [statusHelp, setStatusHelp] = useState<StatusHelpContent>();

  useEffect(() => {
    if (!assortmentType) {
      return;
    }

    setFilters(
      ASSORTMENT_FILTERS[assortmentType].map((filter) => ({
        ...filter,
        selected: filter.status === locationState.filter
      }))
    );
    headerData?.({
      icon: <InfoCircleOutlined aria-hidden />,
      action: () => setIsGuideOpen(true),
      iconTitle: 'راهنما'
    });

    if (vendorId) {
      if (assortmentType === 'assortment region') {
        void dispatch(getAssortmentRegion({vendorId}));
      } else {
        void dispatch(getAssortmentVendor({vendorId}));
      }
    }

    return () => {
      dispatch(resetGetAssortmentItems());
    };
  }, [assortmentType, dispatch, headerData, locationState.filter, vendorId]);

  const sourceItems =
    assortmentType === 'assortment region' ? assortmentRegionItems : assortmentVendorItems;
  const selectedFilter = filters.find((filter) => filter.selected);
  const visibleItems = useMemo(
    () =>
      selectedFilter
        ? sourceItems.filter((item) => item.status === selectedFilter.status)
        : sourceItems,
    [selectedFilter, sourceItems]
  );
  const loading =
    assortmentType === 'assortment region'
      ? gettingAssortmentRegion
      : gettingAssortmentVendor;
  const requestSucceeded =
    assortmentType === 'assortment region'
      ? assortmentRegionSuccess
      : assortmentVendorSuccess;

  const handleFilterChange = (status: AssortmentStatus, selected: boolean) => {
    setFilters((currentFilters) =>
      currentFilters.map((filter) => ({
        ...filter,
        selected: filter.status === status ? selected : false
      }))
    );
  };

  const handleStatusClick = (item: AssortmentItem) => {
    const helpContent = ASSORTMENT_STATUS_HELP[item.status];
    if (helpContent) {
      setStatusHelp(helpContent);
    }
  };

  if (!assortmentType) {
    return null;
  }

  return (
    <>
      <PageLayout
        header={
          <div className="flex flex-col">
            <h1 className="px-4 pt-4 pb-2 text-subtitle-md text-neutral-fg">
              {ASSORTMENT_TITLES[assortmentType]}
            </h1>
            <AssortmentFilters filters={filters} onChange={handleFilterChange} />
          </div>
        }
        contentClassName="bg-gray-02 p-3"
      >
        <AssortmentList
          items={visibleItems}
          assortmentType={assortmentType}
          loading={loading}
          requestSucceeded={requestSucceeded}
          onStatusClick={handleStatusClick}
          onDetailsClick={() => setIsGuideOpen(true)}
        />
      </PageLayout>
      <BottomSheet
        defaultHeader
        title={statusHelp?.title}
        show={Boolean(statusHelp)}
        onClose={() => setStatusHelp(undefined)}
      >
        <StatusHelp
          message={statusHelp?.message ?? ''}
          notice={statusHelp?.notice}
          onClose={() => setStatusHelp(undefined)}
        />
      </BottomSheet>

      <Modal show={isGuideOpen} onClose={() => setIsGuideOpen(false)} fullScreen>
        <AssortmentGuide
          items={ASSORTMENT_GUIDES[assortmentType]}
          onClose={() => setIsGuideOpen(false)}
        />
      </Modal>
    </>
  );
};

export default ChortkeShow;
